import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BarChart2, Users, Landmark, Pickaxe, Building, Banknote } from "lucide-react";

const ROLES = [
  { key: "minister", label: "Minister", icon: Landmark },
  { key: "perm_sec", label: "Permanent Secretary", icon: Building },
  { key: "surveyor_general", label: "Surveyor-General", icon: BarChart2 },
  { key: "mining_commissioner", label: "Mining Commissioner", icon: Pickaxe },
  { key: "land_registrar", label: "Land Registrar", icon: Users },
];

const toneColour = (t) => t === "green" ? "#6BB88A" : t === "red" ? "#E67E7E" : t === "blue" ? "#7CBFDC" : "#C99A2E";

export const Performance = () => {
  const [role, setRole] = useState("minister");
  const { data, isLoading } = useQuery({
    queryKey: ["perf", role],
    queryFn: async () => (await api.get(`/performance/${role}`)).data,
  });

  return (
    <div className="space-y-6" data-testid="page-performance">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Executive Performance Dashboards</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Role-based command view</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Purpose-built KPIs for national leadership, surveyor-general, mining commissioner, land registrar and permanent secretary.</div>
      </header>

      <div className="flex flex-wrap gap-2">
        {ROLES.map((r) => (
          <button key={r.key} onClick={() => setRole(r.key)} data-testid={`role-${r.key}`}
                  className={`cad-chip inline-flex items-center gap-1.5 ${role === r.key ? "cad-chip-gold" : ""}`}>
            <r.icon size={12}/> {r.label}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="cad-panel p-6 text-[13px] text-[#B3C2CC]">Loading role dashboard…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.kpis.map((k, i) => (
              <div key={i} className="cad-panel p-5" data-testid={`perf-kpi-${i}`}>
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{k.label}</div>
                <div className="cad-kpi-value text-3xl mt-2" style={{ color: toneColour(k.tone) }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Application mix</div>
              <div className="mt-3 space-y-1.5">
                {data.application_mix.map((a, i) => {
                  const max = Math.max(...data.application_mix.map((x) => x.n));
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-[#DCE3E8] capitalize">{a._id.replace(/_/g, " ")}</span>
                        <span className="font-mono-cad text-[#F6F8FA]">{a.n} ({a.approved} approved)</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                        <div className="h-full bg-[#C99A2E]" style={{ width: `${(a.n / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] flex items-center gap-1.5">
                <Banknote size={12}/> Revenue by province (paid)
              </div>
              <div className="mt-3 space-y-1.5">
                {data.revenue_by_province.map((r, i) => {
                  const max = Math.max(...data.revenue_by_province.map((x) => x.amount));
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-[#DCE3E8]">{r.province}</span>
                        <span className="font-mono-cad text-[#F6F8FA]">${Math.round(r.amount).toLocaleString()}</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                        <div className="h-full bg-[#6BB88A]" style={{ width: `${(r.amount / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Officer activity</div>
            <table className="w-full text-[13px] mt-3">
              <thead>
                <tr className="text-left border-b cad-hairline">
                  <th className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">Officer</th>
                  <th className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">Actions</th>
                </tr>
              </thead>
              <tbody className="cad-tbody">
                {data.officer_activity.map((o, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-[#F6F8FA]">{o._id}</td>
                    <td className="px-3 py-2 font-mono-cad">{o.actions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
